// SideProgressBarComponent.js
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { Box, Typography } from '@mui/material';
import gsap from 'gsap';
import lottie from 'lottie-web';
import burst from '../../assets/lottie/burst.json';
import CheckIcon from '@mui/icons-material/Check';
import EditIcon from '@mui/icons-material/Edit';
import { headingLetterSpacing } from '../../utils/headingTypography';
import { toSentenceCase } from '../../utils/casing';

const CIRCUMFERENCE = 119.38;
const START_OFFSET = CIRCUMFERENCE * 0.25;

// Colors
const ACCENT_COLOR = '#C7CA00'; // animated connector fill (green)
const RING_COLOR = '#d3d134'; // ring + active dot
const TRACK_COLOR = '#D1D1D1'; // track + temp grow line
const TEXT_MUTED = '#B2B2B2';
const LABEL_ACTIVE = '#1C2F3A';
const CHECK_COLOR = '#1a2b49';
const EDIT_COLOR = '#1c2f3a';

// Timings
const GROW_DURATION = 0.5;
const FADE_DURATION = 0.25;
const EASE = 'power2.out';
const EPS = 0.5;

const SideProgressBarComponent = ({ data }) => {
  if (!data || !data.steps || !Array.isArray(data.steps)) return null;

  // Which step is marked active by upstream data?
  const actualActiveIndex = Math.max(
    0,
    data.steps.findIndex((s) => s.activeStep)
  );

  // Internal animated active index
  const [internalActiveIndex, setInternalActiveIndex] =
    useState(actualActiveIndex);

  // UI state
  const [showCheck, setShowCheck] = useState(false);
  const [shouldAnimateNewRing, setShouldAnimateNewRing] = useState(false);
  const [shouldReverseRingTo75, setShouldReverseRingTo75] = useState(false);

  // Refs
  const ringRefs = useRef({});
  const burstRef = useRef(null);
  const bounceContainerRef = useRef(null);
  const connectorRefs = useRef({});
  const prevActualActiveIndexRef = useRef(actualActiveIndex);

  // Per-row expander + inner + left temp grow line (for only-new-description)
  const expanderRefs = useRef({});
  const expanderInnerRefs = useRef({});
  const growLineRefs = useRef({});

  // Track last committed heights for each row (so we animate only new delta)
  const lastHeightsRef = useRef({}); // { [index]: number }

  // Signature of the active step’s description so we react when it changes (lines added)
  const activeDescSignature = useMemo(() => {
    const step = data.steps[actualActiveIndex];
    if (!step || !Array.isArray(step.description)) return '[]';
    return step.description
      .map((d) => (typeof d?.text === 'string' ? d.text : ''))
      .join('|#|');
  }, [data.steps, actualActiveIndex]);

  const getConnectorOffsets = (i, activeIdx) => {
    const prevIdx = activeIdx - 1;
    if (i < prevIdx) return { top: '40px', bottom: '-18px' }; // completed → completed
    if (i === prevIdx) return { top: '38px', bottom: '-18px' }; // completed → active (above active)
    if (i === activeIdx) return { top: '38px', bottom: '-21px' }; // active → pending (below active)
    return { top: '20px', bottom: '-21px' }; // pending → pending
  };

  /* ================== RING ANIMS ================== */
  useEffect(() => {
    if (!shouldAnimateNewRing) return;
    const newRing = ringRefs.current[internalActiveIndex];
    if (newRing) {
      gsap.fromTo(
        newRing,
        { strokeDashoffset: CIRCUMFERENCE },
        { strokeDashoffset: START_OFFSET, duration: 0.9, ease: 'power2.out' }
      );
    }
    setShouldAnimateNewRing(false);
  }, [shouldAnimateNewRing, internalActiveIndex]);

  useEffect(() => {
    if (!shouldReverseRingTo75) return;
    const ring = ringRefs.current[internalActiveIndex];
    if (ring) {
      gsap.fromTo(
        ring,
        { strokeDashoffset: 0 },
        { strokeDashoffset: START_OFFSET, duration: 0.6, ease: 'power2.out' }
      );
    }
    setShouldReverseRingTo75(false);
  }, [shouldReverseRingTo75, internalActiveIndex]);

  /* ================== STEP TRANSITIONS + CONNECTOR ================== */
  useEffect(() => {
    const goingBack = actualActiveIndex < prevActualActiveIndexRef.current;
    const connectorKey = `connector-${Math.min(actualActiveIndex, prevActualActiveIndexRef.current)}`;
    const connector = connectorRefs.current[connectorKey];
    const isInitialLoad =
      actualActiveIndex === prevActualActiveIndexRef.current;

    if (isInitialLoad) {
      const ring = ringRefs.current[actualActiveIndex];
      if (ring) gsap.set(ring, { strokeDashoffset: START_OFFSET });
      return;
    }

    setShowCheck(false);

    if (goingBack) {
      const currentRing = ringRefs.current[prevActualActiveIndexRef.current];
      if (currentRing) {
        gsap.to(currentRing, {
          strokeDashoffset: CIRCUMFERENCE,
          duration: 0.6,
          ease: 'power2.inOut',
          onComplete: () => {
            if (connector) {
              // shrink green fill up
              gsap.set(connector, { transformOrigin: 'top' });
              gsap.to(connector, {
                scaleY: 0,
                opacity: 0,
                duration: 0.6,
                ease: 'power2.inOut',
                onComplete: () => {
                  setShouldReverseRingTo75(true);
                  setInternalActiveIndex(actualActiveIndex);
                  prevActualActiveIndexRef.current = actualActiveIndex;
                },
              });
            } else {
              setShouldReverseRingTo75(true);
              setInternalActiveIndex(actualActiveIndex);
              prevActualActiveIndexRef.current = actualActiveIndex;
            }
          },
        });
      }
      return;
    }

    // Forward
    const prevRing = ringRefs.current[prevActualActiveIndexRef.current];
    if (prevRing)
      gsap.to(prevRing, {
        strokeDashoffset: 0,
        duration: 0.6,
        ease: 'power2.inOut',
      });

    const tl = gsap.timeline({
      onComplete: () => {
        setInternalActiveIndex(actualActiveIndex);
        prevActualActiveIndexRef.current = actualActiveIndex;
        setShouldAnimateNewRing(true);
      },
    });

    // Bounce + check swap
    tl.to(bounceContainerRef.current, {
      scale: 0.6,
      duration: 0.15,
      ease: 'power1.out',
    })
      .to(bounceContainerRef.current, {
        scale: 1.05,
        duration: 0.1,
        ease: 'power1.inOut',
        onStart: () => setShowCheck(true),
      })
      .to(bounceContainerRef.current, { scale: 0.98, duration: 0.1 })
      .to(bounceContainerRef.current, { scale: 1, duration: 0.1 });

    // Confetti burst
    tl.add(() => {
      if (burstRef.current) {
        burstRef.current.innerHTML = '';
        lottie.loadAnimation({
          container: burstRef.current,
          animationData: burst,
          renderer: 'svg',
          loop: false,
          autoplay: true,
        });
      }
    });

    // Connector: scaleY from 0 → 1 (reliable even while offsets change)
    if (connector) {
      gsap.set(connector, {
        opacity: 1,
        scaleY: 0,
        transformOrigin: 'top',
      });
      tl.to(connector, { scaleY: 1, duration: 0.9, ease: 'power2.out' });
    }
  }, [actualActiveIndex]);

  /* ================== ONLY-NEW DESCRIPTION ANIM (ACTIVE STEP) ================== */
  const animateOnlyNew = (index) => {
    const expander = expanderRefs.current[index];
    const inner = expanderInnerRefs.current[index];
    const growLine = growLineRefs.current[index];
    if (!expander || !inner || !growLine) return;

    // Hide green connector during description growth to avoid flash
    const connector = connectorRefs.current[`connector-${index}`];
    if (connector) gsap.set(connector, { opacity: 0 });

    // Current visible height
    const currentHeight =
      typeof lastHeightsRef.current[index] === 'number'
        ? lastHeightsRef.current[index]
        : expander.clientHeight || 0;

    // Target natural height (with new content)
    const targetHeight = inner.scrollHeight || 0;
    const delta = targetHeight - currentHeight;

    if (delta <= EPS) {
      gsap.set(expander, { height: targetHeight, overflow: 'hidden' });
      gsap.set(inner, { autoAlpha: 1 });
      lastHeightsRef.current[index] = targetHeight;
      // re-show connector if it exists
      if (connector) gsap.set(connector, { opacity: 1 });
      return;
    }

    // Stage
    gsap.set(expander, { height: currentHeight, overflow: 'hidden' });
    if (currentHeight <= EPS) gsap.set(inner, { autoAlpha: 0 });
    else gsap.set(inner, { autoAlpha: 1 });

    // Temp grow line is GREY (no green flash)
    gsap.set(growLine, {
      opacity: 1,
      height: 0,
      top: '58px',
      y: currentHeight, // offset so only the new segment shows
      backgroundColor: TRACK_COLOR,
      zIndex: 2,
    });

    const tl = gsap.timeline();
    tl.to(
      expander,
      { height: targetHeight, duration: GROW_DURATION, ease: EASE },
      0
    ).to(growLine, { height: delta, duration: GROW_DURATION, ease: EASE }, 0);

    if (currentHeight <= EPS) {
      tl.to(
        inner,
        { autoAlpha: 1, duration: FADE_DURATION, ease: EASE },
        `>${0.02}`
      );
    }

    tl.add(() => {
      gsap.set(growLine, { height: 0, opacity: 0, y: 0 });
      gsap.set(expander, { height: targetHeight });
      if (connector) gsap.set(connector, { opacity: 1 });
      lastHeightsRef.current[index] = targetHeight;
    });

    return () => tl.kill();
  };

  // Completed steps show their descriptions; future (inactive) steps are hidden.
  useEffect(() => {
    data.steps.forEach((_, i) => {
      const expander = expanderRefs.current[i];
      const inner = expanderInnerRefs.current[i];
      if (!expander || !inner) return;

      const isCompleted = i < internalActiveIndex;
      const isActive = i === internalActiveIndex;

      if (isCompleted) {
        const target = inner.scrollHeight || 0;
        gsap.set(expander, { height: target, overflow: 'hidden' });
        gsap.set(inner, { autoAlpha: 1 });
        lastHeightsRef.current[i] = target;
        return;
      }

      if (isActive) {
        const target = inner.scrollHeight || 0;
        const current = expander.clientHeight || 0;
        lastHeightsRef.current[i] = current; // baseline before anim
        if (target > current + EPS) {
          animateOnlyNew(i);
        } else {
          gsap.set(expander, { height: target, overflow: 'hidden' });
          gsap.set(inner, { autoAlpha: 1 });
          lastHeightsRef.current[i] = target;
        }
        return;
      }

      // Future (inactive & not completed) steps → hidden
      gsap.set(expander, { height: 0, overflow: 'hidden' });
      gsap.set(inner, { autoAlpha: 0 });
      lastHeightsRef.current[i] = 0;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [internalActiveIndex, data.steps.length]);

  // When the active step’s description content changes (line(s) added)
  useEffect(() => {
    const idx = internalActiveIndex;
    if (idx < 0) return;
    animateOnlyNew(idx);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeDescSignature]);

  return (
    <Box
      sx={{ width: '100%', px: 2, py: '2rem', fontFamily: 'Inter, sans-serif' }}
    >
      <Typography
        variant="h6"
        sx={{
          fontWeight: 600,
          fontSize: '1.125rem',
          letterSpacing: headingLetterSpacing('1.125rem'),
          mb: 5,
        }}
      >
        {data.title}
      </Typography>

      <Box sx={{ display: 'flex', flexDirection: 'column' }}>
        {data.steps.map((step, index) => {
          const isLast = index === data.steps.length - 1;
          const isCompleted = index < internalActiveIndex;
          const isActive = index === internalActiveIndex;

          return (
            <Box
              key={index}
              sx={{ display: 'flex', mb: 2, position: 'relative' }}
            >
              {/* LEFT COLUMN */}
              <Box
                sx={{
                  width: '40px',
                  px: '5px',
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  gap: '10px',
                  position: 'relative',
                }}
              >
                <Box
                  sx={{
                    position: 'relative',
                    width: '40px',
                    height: '40px',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                  }}
                  ref={isActive ? bounceContainerRef : null}
                >
                  {isActive && (
                    <Box
                      ref={burstRef}
                      sx={{
                        position: 'absolute',
                        width: 80,
                        height: 80,
                        top: '50%',
                        left: '50%',
                        transform: 'translate(-50%, -50%) scale(3)',
                        zIndex: 3,
                        pointerEvents: 'none',
                      }}
                    />
                  )}

                  {(isActive || isCompleted) && (
                    <Box
                      sx={{
                        position: 'absolute',
                        width: '40px',
                        height: '40px',
                        top: '50%',
                        left: '50%',
                        transform: 'translate(-50%, -50%)',
                        zIndex: 1,
                      }}
                    >
                      <svg
                        width="40"
                        height="40"
                        viewBox="0 0 40 40"
                        style={{ transform: 'rotate(-90deg)' }}
                      >
                        <circle
                          ref={(el) => (ringRefs.current[index] = el)}
                          cx="20"
                          cy="20"
                          r="19"
                          fill="none"
                          stroke={RING_COLOR}
                          strokeWidth="2"
                          strokeLinecap="round"
                          strokeDasharray={CIRCUMFERENCE}
                          strokeDashoffset={isCompleted ? 0 : CIRCUMFERENCE}
                        />
                      </svg>
                    </Box>
                  )}

                  <Box
                    sx={{
                      position: 'absolute',
                      top: '50%',
                      left: '50%',
                      transform: 'translate(-50%, -50%)',
                      width: '30px',
                      height: '30px',
                      backgroundColor:
                        isCompleted || isActive ? RING_COLOR : '#fff',
                      borderRadius: '50%',
                      border:
                        !isCompleted && !isActive
                          ? `2px solid ${TRACK_COLOR}`
                          : 'none',
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      zIndex: 2,
                    }}
                  >
                    {isCompleted ? (
                      <CheckIcon
                        sx={{ fontSize: '1.125rem', color: CHECK_COLOR }}
                      />
                    ) : isActive ? (
                      showCheck ? (
                        <CheckIcon
                          sx={{ fontSize: '1.125rem', color: CHECK_COLOR }}
                        />
                      ) : (
                        <EditIcon
                          sx={{ fontSize: '1rem', color: EDIT_COLOR }}
                        />
                      )
                    ) : null}
                  </Box>
                </Box>

                {/* Static connectors between steps */}
                {!isLast && (
                  <Box
                    sx={{
                      width: '2px',
                      position: 'absolute',
                      left: '50%',
                      transform: 'translateX(-50%)',
                      zIndex: 0,
                      ...getConnectorOffsets(index, internalActiveIndex),
                    }}
                  >
                    {/* grey track */}
                    <Box
                      sx={{
                        position: 'absolute',
                        width: '2px',
                        top: 0,
                        bottom: 0,
                        backgroundColor: TRACK_COLOR,
                        zIndex: 0,
                      }}
                    />
                    {/* animated green fill — scaleY anim */}
                    <Box
                      ref={(el) =>
                        (connectorRefs.current[`connector-${index}`] = el)
                      }
                      sx={{
                        position: 'absolute',
                        width: '2px',
                        top: 0,
                        bottom: 0, // ensures it always spans container height
                        height: '100%',
                        backgroundColor: ACCENT_COLOR,
                        opacity: 0,
                        transform: 'scaleY(0)',
                        transformOrigin: 'top',
                        zIndex: 1, // above grey track
                        borderRadius: '2px',
                      }}
                    />
                  </Box>
                )}

                {/* Per-row TEMP grow line (grey) that only shows the new segment added */}
                <Box
                  ref={(el) => (growLineRefs.current[index] = el)}
                  sx={{
                    position: 'absolute',
                    width: '2px',
                    top: '58px',
                    left: '50%',
                    transform: 'translateX(-50%)',
                    height: 0,
                    backgroundColor: TRACK_COLOR,
                    opacity: 0,
                    borderRadius: '2px',
                    zIndex: 2,
                  }}
                />
              </Box>

              {/* RIGHT COLUMN */}
              <Box sx={{ ml: 1, flexGrow: 1, pt: '2px' }}>
                {/* Label row */}
                <Box
                  sx={{
                    display: 'flex',
                    alignItems: 'center',
                    gap: '4px',
                    mt: isActive || isCompleted ? '0px' : '-2px',
                    minHeight: '30px',
                  }}
                >
                  <Typography
                    sx={{
                      fontSize: '0.875rem',
                      fontFamily: 'Inter, sans-serif',
                      fontWeight: isActive || isCompleted ? 600 : 400,
                      color:
                        isActive || isCompleted ? LABEL_ACTIVE : TRACK_COLOR,
                      lineHeight: '1.25rem',
                      letterSpacing: '-0.02em',
                    }}
                  >
                    {toSentenceCase(step.label)}
                  </Typography>

                  {isCompleted && (
                    <Box
                      sx={{
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                      }}
                      onClick={() =>
                        data.switchFlow?.(
                          step?.editIcon?.product,
                          step?.editIcon?.questionNumber,
                          {
                            ...(step?.editIcon?.category
                              ? { category: step.editIcon.category }
                              : {}),
                            ...(step?.editIcon?.forceComboQuestionReview
                              ? {
                                  forceComboQuestionReview:
                                    step.editIcon.forceComboQuestionReview,
                                }
                              : {}),
                          }
                        )
                      }
                      title="Edit this section"
                    >
                      <span
                        className="material-symbols-rounded"
                        style={{ fontWeight: 500, fontSize: '1rem' }}
                      >
                        edit
                      </span>
                    </Box>
                  )}
                </Box>

                {/* EXPANDER: active + completed visible, future hidden */}
                <Box
                  ref={(el) => (expanderRefs.current[index] = el)}
                  sx={{
                    height: 0,
                    overflow: 'hidden',
                    willChange: 'height',
                    display: isActive || isCompleted ? 'block' : 'none',
                  }}
                >
                  <Box
                    ref={(el) => (expanderInnerRefs.current[index] = el)}
                    sx={{ py: 0.5 }}
                  >
                    {(isActive || isCompleted) &&
                      Array.isArray(step.description) &&
                      step.description
                        .filter(
                          (d) =>
                            typeof d?.text === 'string' &&
                            d.text.trim().length > 0
                        )
                        .map((desc, i) => (
                          <Typography
                            key={i}
                            sx={{
                              fontFamily: 'Inter, sans-serif',
                              fontWeight: 400,
                              fontSize: '14px',
                              lineHeight: '140%',
                              color: TEXT_MUTED,
                              mb: '2px',
                              pl: '10px',
                            }}
                          >
                            {desc.text}
                          </Typography>
                        ))}
                  </Box>
                </Box>
              </Box>
            </Box>
          );
        })}
      </Box>
    </Box>
  );
};

export default SideProgressBarComponent;
